/**
 * Lead Generation Workbench bulk assign and reason actions.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";
import { leadFactoryLeadGenerationWorkbenchBulkActionUrl } from "./dashboard-urls.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";

export function selectedLeadGenerationLeadIds(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>("[data-lead-generation-select]:checked"))
    .map((checkbox) => checkbox.value.trim())
    .filter(Boolean);
}

export function updateLeadGenerationSelectedCount(): void {
  const countElement = document.querySelector<HTMLElement>("[data-lead-generation-selected-count]");
  if (!countElement) return;
  const selectedCount = selectedLeadGenerationLeadIds().length;
  countElement.textContent = `${formatInteger(selectedCount)} selected`;
}

export function bindLeadGenerationBulkActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  const workbench = document.querySelector<HTMLElement>("[data-lead-generation-workbench]");
  if (!workbench) return;
  workbench.querySelectorAll<HTMLInputElement>("[data-lead-generation-select]").forEach((checkbox) => {
    checkbox.addEventListener("change", updateLeadGenerationSelectedCount);
  });
  workbench.querySelectorAll<HTMLButtonElement>("[data-lead-generation-bulk-action]").forEach((button) => {
    button.addEventListener("click", () => {
      const action = button.dataset.leadGenerationBulkAction;
      if (action) void submitLeadGenerationBulkAction(action, statusElement, actions);
    });
  });
  updateLeadGenerationSelectedCount();
}

export async function submitLeadGenerationBulkAction(
  action: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const leadIds = selectedLeadGenerationLeadIds();
  if (leadIds.length === 0) {
    statusElement.textContent = "Bulk action cancelled; select at least one lead.";
    return;
  }
  const assignedTo = document.querySelector<HTMLInputElement>("[data-lead-generation-bulk-assignee]")?.value.trim() || getDashboardOperator() || "";
  const reason = document.querySelector<HTMLSelectElement>("[data-lead-generation-bulk-reason]")?.value.trim() || "other_typed_review";
  if (action === "assign" && !assignedTo) {
    statusElement.textContent = "Bulk assignment cancelled; enter an assignee.";
    return;
  }
  statusElement.textContent = `Bulk ${labelize(action)} running for ${formatInteger(leadIds.length)} leads...`;
  try {
    const response = await authenticatedFetch(leadFactoryLeadGenerationWorkbenchBulkActionUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action,
        leadIds,
        assignedTo,
        reason,
        note: `Dashboard bulk ${labelize(action)} from Lead Generation Workbench.`
      })
    });
    const body = (await response.json()) as {
      error?: string;
      affectedLeadCount?: number;
      skippedLeads?: unknown[];
      missingLeadIds?: string[];
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Bulk ${labelize(action)}: ${formatInteger(body.affectedLeadCount ?? 0)} updated, ${formatInteger(body.skippedLeads?.length ?? 0)} skipped, ${formatInteger(body.missingLeadIds?.length ?? 0)} missing. Refreshing...`;
    document.querySelectorAll<HTMLInputElement>("[data-lead-generation-select]:checked").forEach((checkbox) => {
      checkbox.checked = false;
    });
    updateLeadGenerationSelectedCount();
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Lead generation bulk action failed.";
  }
}
